import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { palette, radius, shadow, spacing, typography } from '../../../../style-utils/theme';
import { Collapse, DURATION } from '../../../../motion';
import { AppButton } from '../../../button/app-button';
import { toast } from '../../../../utils/toast';
import { readPassError } from '../../../../utils/pass-errors';
import { countOf } from '../../../../function-utils/plural';
import { describeWindow } from '../../../../utils/pass-schedule';
import { usePortalLiveRefresh } from '../../../hooks/use-portal-live-refresh';
import {
  createPass,
  dismissSuggestion,
  listSuggestions,
} from '../../../../api/tenant-passes-client';
import {
  PassHome,
  PassLimits,
  PassSuggestion,
  ResidentPass,
  ResidentPassHome,
} from '../../../../interfaces/visitor-pass';
import { PassEditorModal } from '../../../pass-editor/pass-editor';
import { LIVE_VISITOR_EVENTS, usePanel } from '../portal-common';
import { buildingToday } from './pass-card';

/**
 * LOBBERO REMEMBERS (resident-portal Plan 5, RP-5.3).
 *
 * "Ines has visited on 3 Thursdays. Make this a regular visit?" — above the
 * Expected list, at most three at a time. The server works it out from the
 * household's own visitor list (src/passes/patterns.js) and sends the FIRST
 * NAME only; `key_token` is opaque and only ever sent back to dismiss.
 *
 * NOT AN IQ SURFACE. The shape is IqCleanupHint's (radius.md, shadow.sm, the
 * hairline), but on the plain `surface`: aurora is reserved for model output
 * (UI-CONTRACT §1), and this is a count, not a model.
 *
 *   Make regular  the shared pass editor, ADD mode, opened already filled
 *                 (first name, weekly, the days, the usual window, the home,
 *                 ends in three months) with the focus on Last name.
 *   Not now       remembered for the household, for that home (migration 123).
 *
 * Silent when there is nothing to say: no suggestion, the feature failing, or
 * the list still loading all render nothing — an empty "suggestions" box is
 * noise on a resident's phone.
 */

export const SUGGESTIONS_LABEL = 'Suggestions';
export const MAKE_REGULAR_LABEL = 'Make regular';
export const NOT_NOW_LABEL = 'Not now';
export const MAX_SHOWN = 3;

const WEEKDAYS_PLURAL = [
  'Mondays',
  'Tuesdays',
  'Wednesdays',
  'Thursdays',
  'Fridays',
  'Saturdays',
  'Sundays',
];

const joinAnd = (parts: string[]): string =>
  parts.length <= 1
    ? parts.join('')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/** "Ines has visited on 3 Thursdays. Make this a regular visit?" /
 *  "Rosa has visited 12 times on Mondays, Wednesdays and Fridays. …" */
export const suggestionText = (s: Pick<PassSuggestion, 'first_name' | 'days' | 'evidence'>): string => {
  const name = s.first_name || 'A visitor';
  const days = (s.days || []).filter(d => d >= 1 && d <= 7);
  const ask = 'Make this a regular visit?';
  if (days.length === 1) {
    const day = WEEKDAYS_PLURAL[days[0] - 1];
    const n = s.evidence?.dates || s.evidence?.visits || 0;

    return `${name} has visited on ${n} ${n === 1 ? day.slice(0, -1) : day}. ${ask}`;
  }

  return `${name} has visited ${countOf(s.evidence?.visits || 0, 'time')} on ${joinAnd(
    days.map(d => WEEKDAYS_PLURAL[d - 1])
  )}. ${ask}`;
};

/** "Usually 09:00–13:00" / "At different times of day", plus the home when
 *  the resident has more than one. */
export const suggestionMeta = (s: PassSuggestion, manyHomes: boolean): string => {
  const window = describeWindow(s.window_start, s.window_end);
  const when = window === 'any time' ? 'At different times of day' : `Usually ${window}`;
  if (!manyHomes) return when;
  const home = s.unit_label ? `${s.building_name}, unit ${s.unit_label}` : s.building_name;

  return `${when} · ${home}`;
};

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: 0 0 ${spacing.md};
  min-width: 0;
`;

/* IqCleanupHint's shape on the plain surface — never aurora. */
export const SuggestionPanel = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  padding: ${spacing.sm};
`;

const Text = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const Meta = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

interface SuggestionCardProps {
  suggestion: PassSuggestion;
  manyHomes: boolean;
  busy: boolean;
  onMakeRegular: () => void;
  onNotNow: () => void;
}

export const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  manyHomes,
  busy,
  onMakeRegular,
  onNotNow,
}) => {
  const name = suggestion.first_name || 'this visitor';

  return (
    <SuggestionPanel aria-label={`Suggestion: ${name}`} data-suggestion={suggestion.first_name}>
      <Text>{suggestionText(suggestion)}</Text>
      <Meta>{suggestionMeta(suggestion, manyHomes)}</Meta>
      <Actions>
        <AppButton
          size="compact"
          onClick={onMakeRegular}
          disabled={busy}
          aria-label={`${MAKE_REGULAR_LABEL}: ${name}`}
        >
          {MAKE_REGULAR_LABEL}
        </AppButton>
        <AppButton
          size="compact"
          variant="ghost"
          onClick={onNotNow}
          loading={busy}
          loadingLabel="Hiding…"
          aria-label={`${NOT_NOW_LABEL}: ${name}`}
        >
          {NOT_NOW_LABEL}
        </AppButton>
      </Actions>
    </SuggestionPanel>
  );
};

/* The suggestion list never fails loudly: a load that throws is an empty list.
   Wrapped so a loader that returns nothing (a mocked client) is one too. */
const loadSuggestions = (): Promise<PassSuggestion[]> =>
  Promise.resolve()
    .then(() => listSuggestions())
    .then(list => (Array.isArray(list) ? list : []));

/* WHICH CARD IS WHICH (RP-9.10).
   `key_token` is the server's identity for a suggestion, but it is fresh
   ciphertext on every read (a random IV): keying a card by it would rebuild
   every card each time the tab is returned to. So a card's key is what a
   re-read keeps — the home, the first name and the pattern — and two
   suggestions alike in all of those (different surnames, which never leave
   the server) are told apart by a number: "…", "…#1".

   THE NUMBER MUST NOT SLIDE. It used to restart at every read, so after the
   first of two alike was dismissed, the one that stayed was read back as "…"
   — the key of the card just sent away — and went with it (QA Q-24): one "Not
   now" hid both. `taken` is the keys already answered in this visit to the
   page; a suggestion never takes one of them, so the twin that stays keeps
   "…#1" and its card does not move. */
export const suggestionKeys = (
  list: PassSuggestion[],
  taken: ReadonlySet<string> = new Set()
): string[] => {
  const used = new Set<string>();

  return list.map(s => {
    const base = [s.occupancy_id, s.first_name, (s.days || []).join(''), s.window_start || ''].join('|');
    let n = 0;
    let key = base;
    while (taken.has(key) || used.has(key)) {
      n += 1;
      key = `${base}#${n}`;
    }
    used.add(key);

    return key;
  });
};

export const SUGGESTION_GONE = 'That suggestion is not here any more.';

const toEditorHome = (home: ResidentPassHome): PassHome => ({
  occupancy_id: home.occupancy_id,
  unit_id: home.unit_id,
  unit_label: home.unit_label,
  building_name: home.building_name,
  building_timezone: home.building_timezone,
});

interface PassSuggestionsProps {
  /** The Expected list's homes and limits: the editor needs both. */
  homes: ResidentPassHome[];
  limits: PassLimits | null | undefined;
  /** A pass was saved from a suggestion: the Expected list re-reads. */
  onPassSaved: () => void;
}

export const PassSuggestions: React.FC<PassSuggestionsProps> = ({
  homes,
  limits,
  onPassSaved,
}) => {
  const panel = usePanel(loadSuggestions);
  usePortalLiveRefresh(LIVE_VISITOR_EVENTS, panel.reload);
  // Cards answered in this visit to the page, by key: Collapse plays the exit,
  // the next read drops them, and no later suggestion takes their key.
  const [gone, setGone] = useState<Set<string>>(() => new Set());
  const rereadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (rereadTimer.current) clearTimeout(rereadTimer.current);
    },
    []
  );
  const [busy, setBusy] = useState<string | null>(null);
  // The suggestion the editor was opened from. Kept after it closes, so the
  // modal can animate out; `editorOpen` is what opens and closes it.
  const [editing, setEditing] = useState<{ suggestion: PassSuggestion; key: string } | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  const hide = useCallback((key: string) => {
    setGone(current => new Set(current).add(key));
  }, []);

  /* After a card has folded away, read the list again: the answered one is
     gone for good, and a suggestion that was waiting behind the three shown
     moves up. Not at once — the fold is given its time first. */
  const { reload } = panel;
  const rereadSoon = useCallback(() => {
    if (rereadTimer.current) clearTimeout(rereadTimer.current);
    rereadTimer.current = setTimeout(() => {
      rereadTimer.current = null;
      reload();
    }, DURATION.emphasis);
  }, [reload]);

  const shown = (panel.data || []).slice(0, MAX_SHOWN);
  const keys = suggestionKeys(shown, gone);
  const manyHomes = new Set(shown.map(s => s.occupancy_id)).size > 1 || homes.length > 1;
  const editingHome = editing
    ? homes.find(h => h.occupancy_id === editing.suggestion.occupancy_id)
    : undefined;

  const notNow = async (s: PassSuggestion, key: string) => {
    if (busy) return;
    setBusy(key);
    try {
      await dismissSuggestion(s);
      hide(key);
      toast.success(`We won't suggest ${s.first_name || 'this visitor'} for now.`);
      rereadSoon();
    } catch (error) {
      const reading = readPassError(error, {
        fallback: 'Could not hide that suggestion. Try again.',
        offline: 'Could not hide that suggestion. Check your connection and try again.',
        notFound: SUGGESTION_GONE,
      });
      toast.error(reading.message);
      // Already gone (answered on another phone): show what is true now.
      if (reading.gone) panel.reload();
    } finally {
      setBusy(null);
    }
  };

  if (!shown.length && !editing) return null;

  return (
    <>
      {shown.length ? (
        <Stack role="region" aria-label={SUGGESTIONS_LABEL}>
          {shown.map((s, i) => (
            <Collapse key={keys[i]} open={!gone.has(keys[i])}>
              <SuggestionCard
                suggestion={s}
                manyHomes={manyHomes}
                busy={busy === keys[i]}
                onMakeRegular={() => {
                  setEditorKey(n => n + 1);
                  setEditing({ suggestion: s, key: keys[i] });
                  setEditorOpen(true);
                }}
                onNotNow={() => void notNow(s, keys[i])}
              />
            </Collapse>
          ))}
        </Stack>
      ) : null}

      {editing && editingHome && limits ? (
        <PassEditorModal<ResidentPass>
          key={editorKey}
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          mode="resident"
          homes={[toEditorHome(editingHome)]}
          limits={limits}
          today={buildingToday(editingHome.building_timezone)}
          zone={editingHome.building_timezone}
          prefill={{
            first_name: editing.suggestion.first_name,
            schedule_kind: 'weekly',
            days: editing.suggestion.days,
            window_start: editing.suggestion.window_start,
            window_end: editing.suggestion.window_end,
            valid_from: editing.suggestion.valid_from,
            valid_until: editing.suggestion.valid_until,
          }}
          onSubmit={(spec, extra) =>
            createPass(
              extra.host_occupancy_id ? { ...spec, host_occupancy_id: extra.host_occupancy_id } : spec
            )
          }
          onSaved={() => {
            hide(editing.key);
            setEditorOpen(false);
            onPassSaved();
            panel.reload();
          }}
        />
      ) : null}
    </>
  );
};
